import {useEffect, useRef, useState, useCallback, MouseEvent} from 'react';
import {GhostBan} from 'ghostban';
import {Mode} from '@types';
import type {
    ScenarioMetadata,
    RectangleArea,
    ScenarioMetadataAreaField,
} from '@types';
import {a1ToPos, posToA1, normalizeArea} from '@utils';

type Props = {
    board: GhostBan | null;
    mode: Mode;
    metadata: ScenarioMetadata;
    onToggleTargetPosition: (a1: string) => void;
    onAddArea: (field: ScenarioMetadataAreaField, area: RectangleArea) => void;
};

const modeToAreaField: Partial<Record<Mode, ScenarioMetadataAreaField>> = {
    [Mode.PlayerAllowedArea]: 'playerAllowedAreas',
    [Mode.PlayerDisallowedArea]: 'playerDisallowedAreas',
    [Mode.ComputerAllowedArea]: 'computerAllowedAreas',
    [Mode.ComputerDisallowedArea]: 'computerDisallowedAreas',
};

const AREA_STYLES: Record<
    ScenarioMetadataAreaField,
    {fill: string; stroke: string; dash?: string}
> = {
    playerAllowedAreas: {
        fill: 'rgba(34, 197, 94, 0.15)',
        stroke: 'rgba(34, 197, 94, 0.8)',
    },
    playerDisallowedAreas: {
        fill: 'rgba(239, 68, 68, 0.15)',
        stroke: 'rgba(239, 68, 68, 0.8)',
    },
    computerAllowedAreas: {
        fill: 'rgba(34, 197, 94, 0.15)',
        stroke: 'rgba(34, 197, 94, 0.8)',
        dash: '6 3',
    },
    computerDisallowedAreas: {
        fill: 'rgba(239, 68, 68, 0.15)',
        stroke: 'rgba(239, 68, 68, 0.8)',
        dash: '6 3',
    },
};

const isMetadataMode = (mode: Mode): boolean =>
    mode === Mode.TargetPosition || !!modeToAreaField[mode];

const isAreaMode = (mode: Mode): boolean => !!modeToAreaField[mode];

const getBoardLayout = (board: GhostBan) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canvas = (board as any).canvas as HTMLCanvasElement | undefined;
    if (!canvas) return null;

    const {space, scaledPadding} = board.calcSpaceAndPadding();
    const dpr = canvas.width / canvas.clientWidth;

    return {
        space: space / dpr,
        scaledPadding: scaledPadding / dpr,
        width: canvas.clientWidth,
        height: canvas.clientHeight,
    };
};

export const BoardScenarioMetadataOverlay = ({
    board,
    mode,
    metadata,
    onToggleTargetPosition,
    onAddArea,
}: Props) => {
    const svgRef = useRef<SVGSVGElement>(null);
    const [layout, setLayout] =
        useState<ReturnType<typeof getBoardLayout>>(null);
    const [dragStart, setDragStart] = useState<{
        x: number;
        y: number;
    } | null>(null);
    const [dragCurrent, setDragCurrent] = useState<{
        x: number;
        y: number;
    } | null>(null);

    useEffect(() => {
        if (!board) return;

        const updateLayout = () => setLayout(getBoardLayout(board));
        updateLayout();

        // Observe resize
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const canvas = (board as any).canvas as HTMLCanvasElement | undefined;
        if (!canvas) return;

        const observer = new ResizeObserver(updateLayout);
        observer.observe(canvas);
        return () => observer.disconnect();
    }, [board]);

    // Convert pixel position relative to SVG to nearest pos {x, y}
    const pixelToPos = useCallback(
        (clientX: number, clientY: number): {x: number; y: number} | null => {
            if (!layout || !svgRef.current) return null;

            const rect = svgRef.current.getBoundingClientRect();
            const px = clientX - rect.left;
            const py = clientY - rect.top;

            const x = Math.round((px - layout.scaledPadding) / layout.space);
            const y = Math.round((py - layout.scaledPadding) / layout.space);

            if (x < 0 || x > 18 || y < 0 || y > 18) return null;
            return {x, y};
        },
        [layout]
    );

    const handleClick = useCallback(
        (e: MouseEvent<SVGSVGElement>) => {
            if (mode !== Mode.TargetPosition) return;

            const pos = pixelToPos(e.clientX, e.clientY);
            if (!pos) return;

            const a1 = posToA1(pos.x, pos.y);
            if (a1) onToggleTargetPosition(a1);
        },
        [mode, pixelToPos, onToggleTargetPosition]
    );

    const handleMouseDown = useCallback(
        (e: MouseEvent<SVGSVGElement>) => {
            if (!isAreaMode(mode)) return;
            e.preventDefault();

            const pos = pixelToPos(e.clientX, e.clientY);
            if (!pos) return;

            setDragStart(pos);
            setDragCurrent(pos);
        },
        [mode, pixelToPos]
    );

    const handleMouseMove = useCallback(
        (e: MouseEvent<SVGSVGElement>) => {
            if (!dragStart || !isAreaMode(mode)) return;

            const pos = pixelToPos(e.clientX, e.clientY);
            if (pos) setDragCurrent(pos);
        },
        [dragStart, mode, pixelToPos]
    );

    const handleMouseUp = useCallback(
        (e: MouseEvent<SVGSVGElement>) => {
            if (!dragStart || !isAreaMode(mode)) return;

            const pos = pixelToPos(e.clientX, e.clientY);
            const end = pos || dragCurrent;
            if (!end) {
                setDragStart(null);
                setDragCurrent(null);
                return;
            }

            const a1Start = posToA1(dragStart.x, dragStart.y);
            const a1End = posToA1(end.x, end.y);
            if (a1Start && a1End) {
                const area = normalizeArea(a1Start, a1End);
                const field = modeToAreaField[mode];
                if (field) onAddArea(field, area);
            }

            setDragStart(null);
            setDragCurrent(null);
        },
        [dragStart, dragCurrent, mode, pixelToPos, onAddArea]
    );

    if (!layout) return null;

    const {space, scaledPadding} = layout;

    // Convert pos {x, y} to SVG pixel coordinate */
    const posToPixel = (x: number, y: number) => ({
        px: scaledPadding + x * space,
        py: scaledPadding + y * space,
    });

    // Render a single area rectangle
    const renderArea = (
        area: RectangleArea,
        style: (typeof AREA_STYLES)[ScenarioMetadataAreaField],
        key: string
    ) => {
        const minPos = a1ToPos(area.min);
        const maxPos = a1ToPos(area.max);
        if (minPos.x < 0 || minPos.y < 0 || maxPos.x < 0 || maxPos.y < 0)
            return null;

        const topY = Math.min(minPos.y, maxPos.y);
        const bottomY = Math.max(minPos.y, maxPos.y);
        const leftX = Math.min(minPos.x, maxPos.x);
        const rightX = Math.max(minPos.x, maxPos.x);

        const topLeft = posToPixel(leftX, topY);
        const bottomRight = posToPixel(rightX, bottomY);

        return (
            <rect
                key={key}
                x={topLeft.px - space * 0.5}
                y={topLeft.py - space * 0.5}
                width={bottomRight.px - topLeft.px + space}
                height={bottomRight.py - topLeft.py + space}
                fill={style.fill}
                stroke={style.stroke}
                strokeWidth={2}
                strokeDasharray={style.dash}
                rx={3}
            />
        );
    };

    // Render drag preview rectangle
    const renderDragPreview = () => {
        if (!dragStart || !dragCurrent || !isAreaMode(mode)) return null;

        const field = modeToAreaField[mode];
        if (!field) return null;

        const style = AREA_STYLES[field];
        const topY = Math.min(dragStart.y, dragCurrent.y);
        const bottomY = Math.max(dragStart.y, dragCurrent.y);
        const leftX = Math.min(dragStart.x, dragCurrent.x);
        const rightX = Math.max(dragStart.x, dragCurrent.x);

        const topLeft = posToPixel(leftX, topY);
        const bottomRight = posToPixel(rightX, bottomY);

        return (
            <rect
                x={topLeft.px - space * 0.5}
                y={topLeft.py - space * 0.5}
                width={bottomRight.px - topLeft.px + space}
                height={bottomRight.py - topLeft.py + space}
                fill={style.fill}
                stroke={style.stroke}
                strokeWidth={2.5}
                strokeDasharray={style.dash || '4 2'}
                rx={3}
                opacity={0.7}
            />
        );
    };

    return (
        <svg
            ref={svgRef}
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: layout.width,
                height: layout.height,
                pointerEvents: isMetadataMode(mode) ? 'auto' : 'none',
                zIndex: 10,
                cursor: isAreaMode(mode)
                    ? 'crosshair'
                    : mode === Mode.TargetPosition
                      ? 'pointer'
                      : 'default',
            }}
            onClick={handleClick}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={() => {
                if (dragStart) {
                    setDragStart(null);
                    setDragCurrent(null);
                }
            }}>
            {(Object.keys(AREA_STYLES) as ScenarioMetadataAreaField[]).map(
                field =>
                    metadata[field].map((area, idx) =>
                        renderArea(area, AREA_STYLES[field], `${field}-${idx}`)
                    )
            )}

            {renderDragPreview()}

            {metadata.targetPositions.map(a1 => {
                const pos = a1ToPos(a1);
                if (pos.x < 0 || pos.y < 0) return null;
                const pixel = posToPixel(pos.x, pos.y);
                const r = space * 0.3;
                return (
                    <g key={`target-${a1}`}>
                        <polygon
                            points={`${pixel.px},${pixel.py - r} ${pixel.px + r},${pixel.py} ${pixel.px},${pixel.py + r} ${pixel.px - r},${pixel.py}`}
                            fill="rgba(251, 191, 36, 0.6)"
                            stroke="rgba(245, 158, 11, 0.9)"
                            strokeWidth={1.5}
                        />
                    </g>
                );
            })}
        </svg>
    );
};
